<template>
  <v-form
    v-if="appointment"
    ref="form">
    <tab-title
      :car-id="appointment.car.id"
      text="基本情報" />
    <cp-radio
      v-if="!fetchingPdf"
      v-model="showPdf"
      :items="shakenshoOptions"
      :rules="[$rules.isRequired]"
      :label="'車のタイプを選択してください*'"
      @change="(value) => {
        showPdf = value
      }"
    />
    <cp-pdf
      v-if="!fetchingPdf && showPdf === '1'"
      :label="'検査記録事項のアップロード'"
      :required="true"
      :caption="'※ 読み取りアプリを使用し、読み取りデータのアップロードを行ってください'"
      :pdf="shakenshoPdf"
      :confidential="true"
      @upload-error="onError"
      @finished="getShakenshoPdf"
      @load-pdf="openShakenshoPdf"
    />
    <photo-group
      :items="photosList"
      :photos="photos"
      tab="basic"
    />
    <package-picker
      :car-id="appointment.car.id"
      @gradeSelected="gradeSelected"
    />
    <v-select
      :items="brands"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('brandId')} *`"
      v-model="car.brandId"
      data-label-scroll="brandId"
      @change="(value) => {
        updateModel({ field: 'brandId', value })
        modificationOptions = []
        modelOptions = []
      }"
    />
    <v-select
      v-model="car.year"
      :items="yearsOptions"
      :disabled="!yearsOptions.length"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('year')} *`"
      data-label-scroll="year"
      @change="(value) => updateCar({ field: 'year', value })"
    />
    <v-select
      v-model="car.modelId.id"
      :items="modelOptions"
      :loading="modelsLoading"
      :disabled="!modelOptions.length || !car.brandId || !car.year"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('modelId')} *`"
      data-label-scroll="modelId"
      @change="(value) => {
        updateModel({ field: 'modelId', value })
        modificationOptions = []
      }"
    />
    <v-select
      v-model="car.modificationId.id"
      :items="modificationOptions"
      :loading="modificationsLoading"
      :disabled="modificationsLoading || !modificationOptions.length || !car.modelId.id"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('modificationId')} *`"
      data-label-scroll="MODIFICATION_ID"
      @change="(value) => updateModel({ field: 'modificationId', value })"
    />
    <v-text-field
      v-model="car.modificationExt"
      :label="labelOf('modificationExt')"
      data-label-scroll="modificationExt"
      @blur="({ target }) => updateCar({ field: 'modificationExt', value: target.value })"
    />
    <cp-date-picker
      id="date-sts"
      key="date-sts"
      v-model="car.sts"
      :rules="[$rules.isRequired]"
      :show-day="false"
      :label="`${labelOf('sts')} *`"
      type="month"
      data-label-scroll="sts"
      @change="(value) => updateCar({ field: 'sts', value: value ? `${value}-01`: null })"
    />
    <cp-date-picker
      id="date-inspection"
      key="date-inspection"
      v-model="car.inspectionDate"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('inspectionDate')} *`"
      data-label-scroll="inspectionDate"
      @change="(value) => updateCar({ field: 'inspectionDate', value })"
    />
    <v-text-field
      v-model="car.plate"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('plate')} *`"
      data-label-scroll="plate"
      @blur="updateCar({ field: 'plate', value: car.plate })"
    />
    <v-text-field
      v-model="car.vin"
      :rules="!car.reimportVin ? [$rules.isRequired] : []"
      :disabled="!!car.reimportVin"
      :label="`${labelOf('vin')}${!car.reimportVin ? ' *' : ''}`"
      hint="車台番号かシリアル番号どちらかを入力"
      data-label-scroll="vin"
      @blur="updateCar({ field: 'vin', value: car.vin }); car.reimportVin = null"
    />
    <v-text-field
      v-model="car.reimportVin"
      :rules="!car.vin ? [$rules.isRequired] : []"
      :disabled="!!car.vin"
      :label="`${labelOf('reimportVin')}${!car.vin ? ' *' : ''}`"
      hint="車台番号かシリアル番号どちらかを入力"
      data-label-scroll="reimportVin"
      @blur="updateCar({ field: 'reimportVin', value: car.reimportVin }); car.vin = null"
    />
    <v-text-field
      v-model="car.numberPlate"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('numberPlate')} *`"
      data-label-scroll="numberPlate"
      @blur="updateCar({ field: 'numberPlate', value: car.numberPlate })"
    />
    <v-select
      v-model="car.engineType"
      :items="engineTypeOptions"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('engineType')} *`"
      data-label-scroll="engineType"
      @change="(value) => updateCar({ field: 'engineType', value })"
    />
    <v-text-field
      v-model="car.seats"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('seats')} *`"
      return-masked-value
      data-label-scroll="seats"
      @blur="updateCar({ field: 'seats', value: car.seats })"
    />
    <v-text-field
      v-model="car.engine"
      :rules="[
        $rules.isRequired,
        v => !v || parseFloat(v, 10) <= 10 || '排気量の数字を再確認してください',
      ]"
      :label="`${labelOf('engine')} *`"
      suffix="L"
      mask="#.#######"
      return-masked-value
      type="tel"
      data-label-scroll="engine"
      @blur="updateCar({ field: 'engine', value: car.engine })"
    />
    <v-select
      v-model="car.carHistory"
      :items="carHistoryOptions"
      :rules="[
        $rules.isRequired,
        v => ruleExists(v, carHistoryOptions)
      ]"
      :label="`${labelOf('carHistory')} *`"
      data-label-scroll="carHistory"
      @change="(value) => updateCar({ field: 'carHistory', value })"
    />
    <span data-label-scroll="recyclingDeposit">
      <v-text-field
        v-model="car.recyclingDeposit"
        :rules="[$rules.isRequired]"
        :label="`${labelOf('recyclingDeposit')} *`"
        prefix="¥"
        type="number"
        @blur="updateCar({ field: 'recyclingDeposit', value: car.recyclingDeposit })"
      />
    </span>
    <cp-radio
      v-model="car.serviceBook"
      :items="yesOrNoString"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('serviceBook')} *`"
      hint="新車時付属のもの。それ以外（コピー等）の場合は「なし」を選択。"
      data-label-scroll="serviceBook"
      @change="(value) => updateCar({ field: 'serviceBook', value })"
    />
    <cp-radio
      v-model="car.serviceWarranty"
      :items="yesOrNoString"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('serviceWarranty')} *`"
      hint="新車販売時のもので、正規販売ディーラーの押印のあるものに限る。それ以外は「なし」を選択"
      data-label-scroll="serviceWarranty"
      @change="(value) => updateCar({ field: 'serviceWarranty', value })"
    />
    <cp-radio
      v-model="car.recordBook"
      :items="yesOrNoString"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('recordBook')} *`"
      data-label-scroll="recordBook"
      @change="(value) => updateCar({ field: 'recordBook', value })"
    />
    <cp-radio
      v-model="car.insurance"
      :items="yesOrNoString"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('insurance')} *`"
      data-label-scroll="insurance"
      @change="(value) => updateCar({ field: 'insurance', value })"
    />
    <v-select
      v-model="car.colorId"
      :items="colors"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('colorId')} *`"
      data-label-scroll="colorId"
      @change="(value) => updateCar({ field: 'colorId', value })"
    />
    <v-text-field
      v-model="car.colorOriginal"
      :label="labelOf('colorOriginal')"
      data-label-scroll="colorOriginal"
      @blur="updateCar({ field: 'colorOriginal', value: car.colorOriginal })"
    />
    <cp-radio
      v-model="car.colorChanged"
      :items="yesOrNoString"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('colorChanged')} *`"
      data-label-scroll="colorChanged"
      @change="(value) => updateCar({ field: 'colorChanged', value })"
    />
    <v-select
      v-if="car.colorChanged === '1'"
      v-model="car.originalColorId"
      :items="originalColors"
      :label="`${labelOf('originalColorId')} *`"
      :rules="[$rules.isRequired]"
      data-label-scroll="originalColorId"
      @change="(value) => updateCar({ field: 'originalColorId', value })"
    />
    <cp-radio
      :items="transmissionDriveOptions"
      v-model="car.transmissionDrive"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('transmissionDrive')} *`"
      data-label-scroll="transmissionDrive"
      @change="(value) => updateCar({ field: 'transmissionDrive', value })"
    />
    <mileage-data
      :car="car"
      :appointment="appointment" />
    <v-select
      v-model="car.speedometerChanges"
      :rules="[$rules.isRequired]"
      :items="speedometerChangesOptions"
      :label="`${labelOf('speedometerChanges')} *`"
      data-label-scroll="speedometerChanges"
      @change="(value) => updateCar({ field: 'speedometerChanges', value })"
    />
    <cp-radio
      v-model="car.importOfficial"
      :items="importOptions"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('importOfficial')} *`"
      data-label-scroll="importOfficial"
      @change="(value) => updateCar({ field: 'importOfficial', value })"
    />
    <v-select
      v-model="car.climate"
      :items="climateOptions"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('climate')} *`"
      data-label-scroll="climate"
      @change="(value) => updateCar({ field: 'climate', value })"
    />
    <v-text-field
      v-if="car.climate === 'CLIMATE_ABNORMAL'"
      v-model="car.climateDescription"
      :label="`${labelOf('description')} *`"
      :rules="[$rules.isRequired]"
      data-label-scroll="climateDescription"
      @blur="({ target }) => updateCar({ field: 'climateDescription', value: target.value })"
    />
    <v-select
      v-model="car.transmission"
      :items="transmissionOptions"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('transmission')} *`"
      data-label-scroll="transmission"
      @change="(value) => updateCar({ field: 'transmission', value })"
    />
    <v-select
      v-model="car.transmissionMovement"
      :items="transmissionMovementOptions"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('transmissionMovement')} *`"
      data-label-scroll="transmissionMovement"
      @change="(value) => updateCar({ field: 'transmissionMovement', value })"
    />
    <v-text-field
      v-if="car.transmissionMovement === 'ABNORMAL'"
      v-model="car.transmissionMovementDescription"
      :label="`${labelOf('description')} *`"
      :rules="[$rules.isRequired]"
      data-label-scroll="transmissionMovementDescription"
      @blur="({ target }) => updateCar({
        field: 'transmissionMovementDescription',
        value: target.value
      })"
    />
    <cp-radio
      v-model="car.timingBeltExchangeHistory"
      :items="yesOrNoString"
      :rules="[$rules.isRequired]"
      :label="`${labelOf('timingBeltExchangeHistory')} *`"
      data-label-scroll="timingBeltExchangeHistory"
      @change="(value) => updateCar({ field: 'timingBeltExchangeHistory', value })"
    />
    <span
      v-if="car.timingBeltExchangeHistory && car.timingBeltExchangeHistory !== '0'">
      <cp-date-picker
        key="date-timing-belt"
        v-model="car.timingBeltExchange"
        :label="labelOf('timingBeltExchange')"
        data-label-scroll="timingBeltExchange"
        @change="(value) => updateCar({ field: 'timingBeltExchange', value })"
      />
      <v-text-field
        v-model="car.timingBeltExchangeValue"
        :label="labelOf('timingBeltExchangeValue')"
        type="number"
        data-label-scroll="timingBeltExchangeValue"
        @blur="({ target }) => updateCar({ field: 'timingBeltExchangeValue', value: target.value })"
      />
      <cp-radio
        v-model="car.timingBeltExchangeValueType"
        :items="timingBeltExchangeValueTypeOptions"
        :label="labelOf('timingBeltExchangeValueType')"
        data-label-scroll="timingBeltExchangeValueType"
        @change="(value) => updateCar({ field: 'timingBeltExchangeValueType', value })"
      />
    </span>
    <v-divider class="my-5" />
    <v-btn
      outline
      color="info"
      block
      large
      to="equipment#top">次へ</v-btn>
  </v-form>
</template>
<script>
import {
  GET_BRANDS,
  GET_PHOTOS,
  BASIC_TAB,
  GET_MODELS,
  GET_MODIFICATIONS,
  GET_CAR_TITLE,
  SEARCH_GRADE,
  UPDATE_TYPE,
  GET_SHAKENSHO_PDF,
} from '../../queries';

import PhotoGroup from '../../components/fields/PhotoGroup';
import CpPdf from '../../components/elements/CpPdf';
import PackagePicker from '../../components/PackagePicker';

import {
  yesOrNoNumeric,
  yesOrNoString,
  colors,
  transmissionOptions,
  transmissionDriveOptions,
  importOptions,
  engineTypeOptions,
  climateOptions,
  originalColors,
  transmissionMovementOptions,
  timingBeltExchangeValueTypeOptions,
  speedometerChangesOptions,
  carHistoryOptions,
  labels,
  MODIFICATION_OTHER,
} from '../../options';

import MileageData from '../../components/fields/MileageData';

const photosList = [
  {
    name: 'pts',
    label: '車検証',
    caption: '・記載内容がすべて解るように撮影<br>・白トビや、類別区分番号が切れていたり、折り目で見えない場合は再撮影する',
    required: true,
    confidential: false,
  },
];

const pdfList = [
  {
    name: 'pts_cert_adv',
    label: '自動車検査証記録事項',
    caption: '※読み取りアプリを使用し、読み取りデータのアップロードを行ってください',
    required: false,
    confidential: true,
  },
];

export const shakenshoOptions = [
  {
    text: '自動車車検証',
    value: '0',
  },
  {
    text: '電子車検証',
    value: '1',
  },
];

export default {
  components: {
    MileageData,
    PhotoGroup,
    CpPdf,
    PackagePicker,
  },
  props: {
    appointmentId: {
      type: [String, Number],
      default: '',
    },
  },
  apollo: {
    carBrands: {
      query: GET_BRANDS,
      fetchPolicy: 'network-only',
      prefetch: false,
      result({ data }) {
        this.brands = data.carBrands.data.map(({
          name, id, start, end,
        }) => ({
          text: name, value: id, start, end,
        }));
      },
    },
    photos: {
      query: GET_PHOTOS,
      variables() {
        return {
          id: parseInt(this.$route.params.id, 10),
        };
      },
    },
    getModels: {
      query: GET_MODELS,
      skip: true,
      prefetch: false,
      variables() {
        return {
          brandId: parseInt(this.appointment.car.brandId, 10),
          year: parseInt(this.appointment.car.year, 10),
        };
      },
      result({ data }) {
        this.modelOptions = data.getModels.map(({ id, title }) => ({
          value: id, text: title,
        }));
      },
    },
    carModifications: {
      query: GET_MODIFICATIONS,
      skip() {
        return !this.appointment || !this.appointment.car || !this.appointment.car.modelId.id;
      },
      prefetch: false,
      variables() {
        return {
          modelId: parseInt(this.appointment.car.modelId.id, 10),
          year: this.appointment.car.year.toString(),
        };
      },
      result({ data }) {
        this.modificationOptions = [
          {
            fullTitle: 'その他の仕様',
            id: MODIFICATION_OTHER,
          },
          ...data.carModifications.data,
        ].map(m => ({ text: m.fullTitle, value: m.id }));

        if (!this.modificationOptions
          .find(m => m.value === this.appointment.car.modificationId.id)) {
          this.appointment.car.modificationId.id = null;
        }
      },
    },
    appointment: {
      query: BASIC_TAB,
      fetchPolicy: 'network-only',
      variables() {
        return {
          id: parseInt(this.$route.params.id, 10),
        };
      },
      result({ data }) {
        this.loadYears(data.appointment.car.brandId);
      },
    },
  },
  data() {
    return {
      tab: 'basic',
      showPdf: '1',
      fetchingPdf: true,
      shakenshoPdf: {},
      brandId: '',
      year: '',
      modelId: '',
      shakenshoOptions,
      yesOrNoNumeric,
      yesOrNoString,
      transmissionOptions,
      colors,
      originalColors,
      importOptions,
      climateOptions,
      transmissionMovementOptions,
      timingBeltExchangeValueTypeOptions,
      carHistoryOptions,
      engineTypeOptions,
      transmissionDriveOptions,
      speedometerChangesOptions,
      brands: [],
      yearsOptions: [],
      modelOptions: [],
      modificationOptions: [],
      yearsLoading: false,
      modelsLoading: false,
      modificationsLoading: false,
      photosList,
      pdfList,
    };
  },
  computed: {
    car() {
      return this.appointment.car || {};
    },
  },
  async mounted() {
    await this.getShakenshoPdf();
  },
  methods: {
    onError({ status }) {
      this.$store.commit('setError', status);
    },
    async openShakenshoPdf() {
      await this.getShakenshoPdf();

      if (this.shakenshoPdf.url !== null) {
        window.open(this.shakenshoPdf.url).focus();
      }
    },
    async getShakenshoPdf() {
      this.fetchingPdf = true;

      const { data } = await this.$apollo.query({
        query: GET_SHAKENSHO_PDF,
        variables: { appointmentId: parseInt(this.$route.params.id, 10) },
        fetchPolicy: 'network-only',
      });

      if (data.shakenshoPdf.url !== null) {
        this.shakenshoPdf = data.shakenshoPdf;
        this.showPdf = '1';
      }

      this.fetchingPdf = false;
    },
    labelOf(name) {
      return labels[name] || '';
    },
    loadYears(brandId) {
      if (!brandId) return;

      this.years = [];

      const brand = this.brands.find(b => b.value === parseInt(brandId, 10));

      if (!brand) return;

      const { start, end } = brand;

      for (let i = start; i <= (end || (new Date()).getFullYear()); i += 1) {
        this.years.push({
          text: `${this.calcYear(i)} (${i})`,
          value: i,
        });
      }

      this.yearsOptions = [
        { text: '不明', value: null },
        ...this.years.reverse(),
      ];

      if (this.years.find(year => year.value === this.appointment.car.year)) {
        this.$apollo.queries.getModels.skip = false;
      }
    },
    calcYear(year) {
      if (year <= 1988) return `昭和${year - 1925}年`;
      if (year <= 2018) return `平成${parseInt(year, 10) === 1989 ? '元' : year - 1988}年`;
      return `令和${parseInt(year, 10) === 2019 ? '元' : year - 2018}年`;
    },
    updateModel(params) {
      return this.updateCar({
        ...params,
        refetchQueries: [{
          query: GET_CAR_TITLE,
          variables: { id: this.appointment.id },
        }],
      });
    },
    async gradeSelected({
      brandText,
      modelText,
      gradeText,
      typeText,
    }) {
      const modificationId = await (async () => {
        try {
          const result = await this.$apollo.query({
            query: SEARCH_GRADE,
            fetchPolicy: 'network-only',
            variables: {
              brandText,
              modelText,
              gradeText,
              typeText,
              year: parseInt(this.appointment.car.year, 10),
              appointmentId: parseInt(this.appointment.id, 10),
            },
          });
          return result.data.searchGradeBasedOnCarMasterGrade.data[0].id;
        } catch (error) {
          // If there are errors ignore for now and
          // just set the modification to other
          return MODIFICATION_OTHER;
        }
      })();

      this.$apollo.mutate({
        mutation: UPDATE_TYPE('Car'),
        variables: {
          field: 'modificationId', value: `${modificationId}`, id: this.appointment.car.id,
        },
        refetchQueries: [{
          query: GET_MODIFICATIONS,
          variables: {
            modelId: parseInt(this.appointment.car.modelId.id, 10),
            year: this.appointment.car.year.toString(),
          },
        }, {
          query: BASIC_TAB,
          variables: { id: parseInt(this.$route.params.id, 10) },
        }],
      });
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/tabs/BasicTab.vue